import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * 상태별 면·테두리. 셋은 **배타적으로 하나만** 골라 붙인다(JS에서 선택).
 * disabled에 `disabled:` variant를 쓰지 않는 이유: `hover:` 유틸은 disabled 버튼에도
 * CSS상 그대로 적용돼(포인터 이벤트가 살아 있음) 비활성 카드가 hover에 반응해 버린다.
 * 상태 클래스를 통째로 갈아끼우면 hover 유틸 자체가 붙지 않아 그 문제가 사라진다.
 *
 * Tailwind는 클래스를 **정적 문자열로** 스캔하므로 `border-${tone}` 같은 조합 금지.
 */
const STATE_CLASS = {
  /** 시안 `.site-item.active` — 브랜드 테두리 + 대각 그라데이션 + 옅은 그림자. */
  selected: 'border-brand-500 bg-gradient-to-br from-brand-50 to-surface-soft shadow-card-sm',
  /** 시안 `.site-item` 기본/hover. */
  idle: 'border-line bg-white hover:border-brand-200 hover:bg-surface-soft',
  /** 선택 불가(이미 보고서가 생성된 점검 등). */
  disabled: 'cursor-not-allowed border-line bg-surface-soft opacity-60',
} as const;

/** 아이콘 타일(38px) — 선택 시 흰 배경 + 브랜드 아이콘으로 반전(시안 `.site-item.active .site-icon`). */
const TILE_CLASS = {
  selected: 'bg-white text-brand-600',
  idle: 'bg-surface-soft text-ink-500',
} as const;

/**
 * `title`은 네이티브 `title`(툴팁 문자열)을 덮어쓴다 — 카드 제목은 배지·강조를 품는
 * ReactNode라 `string` 시그니처와 양립하지 않는다(Omit 없이 확장하면 TS2430).
 * 툴팁이 필요하면 소비처가 감싸는 요소에 둔다.
 */
export interface SelectableCardProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  /** 선택 여부. 시각 상태와 `aria-pressed`를 함께 좌우한다. */
  selected: boolean;
  /** 제목(굵은 한 줄). */
  title: ReactNode;
  /** 부제(주소·요약 등). 없으면 렌더하지 않는다. */
  subtitle?: ReactNode;
  /** 아이콘 — 있을 때만 38px 타일을 렌더한다(시안 현장 카드에만 존재). */
  icon?: ReactNode;
  /** 제목·부제 아래 추가 본문(통계 줄 등). */
  children?: ReactNode;
  /** 우측 커스텀 영역(배지 등) — 체크 원 **앞**에 놓인다. */
  trailing?: ReactNode;
  /** 추가 클래스. 기본 클래스 뒤에 붙어 같은 속성이면 우선. */
  className?: string;
}

/**
 * 선택 가능한 카드 — 목록에서 하나(또는 여럿)를 고르는 자리의 **선택 어포던스 단일 소스**.
 *
 * 화면마다 손으로 맞추면 "선택된 카드에 체크 표시가 있는 화면과 없는 화면"처럼 갈라지므로,
 * 테두리·배경·아이콘 타일 반전·체크 원을 여기서만 소유한다(결정
 * `2026-07-27-ui-redesign-mockup-adoption.md`의 시안 `.site-item`/`.inspection-item`).
 *
 * 보장하는 것:
 * - 체크 원(22px)은 **미선택일 때도 자리를 차지**하고 opacity만 전환한다 — 선택할 때
 *   레이아웃이 밀리지 않는다(시안이 `opacity:0`으로 잡아둔 이유).
 * - `type="button"` 기본 — 폼 안에서 의도치 않은 submit 방지(Button과 같은 규약).
 * - 포커스 링은 Button과 같은 축(`focus-visible:ring-brand-500`).
 *
 * ## 접근성: 왜 radio가 아니라 `aria-pressed` 토글 버튼인가
 * 소비처의 선택 의미가 셋이다 — (1) 단일 선택, (2) **선택된 항목 재클릭으로 해제**
 * (TBM 점검 선택: `onInspectionSelect(isSelected ? null : id)`), (3) 다중 선택.
 * radio는 (2)(3)을 표현하지 못하고(체크된 radio는 재클릭으로 해제 불가), `role="radiogroup"`은
 * 부모가 roving tabindex까지 소유해야 성립하는데 leaf 프리미티브가 그걸 보장할 수 없다.
 * `aria-pressed`는 세 경우 모두 정확하고 부모 협조 없이 성립하므로 이쪽을 기본으로 둔다.
 * rest props가 뒤에 펼쳐지므로, 부모가 roving tabindex까지 소유하는 진짜 radiogroup을 짓는
 * 자리라면 `role`·`aria-checked`와 함께 `aria-pressed={undefined}`를 넘겨 덮어쓸 수 있다.
 *
 * 클릭 핸들러는 표준 `onClick`을 그대로 쓴다 — React의 `onSelect`는 이미 DOM select 이벤트라
 * 같은 이름을 선택 콜백으로 덮으면 조용한 혼선이 된다.
 */
export function SelectableCard({
  selected,
  title,
  subtitle,
  icon,
  children,
  trailing,
  disabled = false,
  type = 'button',
  className = '',
  ...rest
}: SelectableCardProps) {
  const state = disabled ? 'disabled' : selected ? 'selected' : 'idle';

  return (
    <button
      type={type}
      disabled={disabled}
      aria-pressed={selected}
      className={`flex w-full items-center gap-3 rounded-card border px-3.5 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 ${STATE_CLASS[state]} ${className}`}
      {...rest}
    >
      {icon && (
        <span
          aria-hidden="true"
          className={`grid h-[38px] w-[38px] flex-none place-items-center rounded-card-sm ${
            selected ? TILE_CLASS.selected : TILE_CLASS.idle
          }`}
        >
          {icon}
        </span>
      )}

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-bold tracking-tight text-ink-900">
          {title}
        </span>
        {subtitle && (
          <span className="mt-0.5 block truncate text-xs font-medium text-ink-500">
            {subtitle}
          </span>
        )}
        {children}
      </span>

      {trailing}

      {/* 체크 원 — 미선택에도 자리를 유지(레이아웃 흔들림 방지). */}
      <span
        aria-hidden="true"
        className={`grid h-[22px] w-[22px] flex-none place-items-center rounded-full bg-brand-500 text-white transition-opacity ${
          selected ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <svg
          className="h-[13px] w-[13px]"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
      </span>
    </button>
  );
}
